import { Component, Input, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ErrorNotificationDialogComponent } from 'src/app/modules/shared/dialogs/error-notification-dialog/error-notification-dialog.component';
import { SectionConfigModel } from '../../models/section-configuration.model';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ConfirmationDialogComponent } from 'src/app/modules/shared/dialogs/confirmation-dialog/confirmation-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import { SnackbarService } from '../../../shared/services/snackbar.service';
import { SectionConfigurationService } from '../../services/section-configuration.service';
import { finalize, take } from 'rxjs';

@Component({
    selector: 'section',
    templateUrl: 'section.component.html',
    styleUrls: ['section.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SectionComponent implements OnInit {
    @Input() sectionConfig: SectionConfigModel;

    form: FormGroup;
    expanded: boolean = false;
    isSubmitted: boolean = false;
    isSaving: boolean = false;
    sectionName: string;

    private sectionNameRequestId = 0;

    constructor(private readonly configService: SectionConfigurationService,
        private dialog: MatDialog,
        private readonly fb: FormBuilder,
        private readonly translateService: TranslateService,
        private snackbarService: SnackbarService) { }

    ngOnInit(): void {
        this.form = this.fb.group({});
        this.setSectionName();
    }

    get canSave(): boolean {
        return this.sectionConfig.bannersConfig?.items?.every(item => !!item.file || item.src) ?? true;
    }

    save(): void {
        if (this.isSaving) {
            return;
        }

        this.form.markAllAsTouched();
        this.isSubmitted = true;

        if (this.form.valid && this.canSave) {
            const data = this.prepareModelToSave(<SectionConfigModel>this.form.getRawValue());
            this.isSaving = true;
            this.configService.updateSectionConfiguration(data).pipe(
                finalize(() => this.isSaving = false)
            ).subscribe({
                next: (response) => {
                    this.sectionConfig = response;
                    this.form.reset(this.sectionConfig);
                    this.setSectionName();
                    this.isSubmitted = false;

                    this.snackbarService.success('COMMON.SAVE_SUCCESS');
                },
                error: () => {
                    this.dialog.open(ErrorNotificationDialogComponent)
                }
            });
        } else {
            this.dialog.open(ErrorNotificationDialogComponent, {
                data: {
                    title: 'Validation errors',
                    message: 'Form contains validation errors. Please fix them and try again.'
                }
            })
        }
    }

    onPanelClosed(): void {
        if (this.form.dirty) {
            this.dialog.open(ConfirmationDialogComponent, {
                data: {
                    title: `${this.sectionName} section contains unsaved changes.`,
                    message: 'Do you want to save?'
                }
            }).afterClosed().subscribe(confirmed => {
                if (confirmed) {
                    this.save();
                }
            });
        }
    }

    private setSectionName(): void {
        const requestId = ++this.sectionNameRequestId;
        if (this.sectionConfig.name) {
            this.sectionName = this.sectionConfig.name
        } else {
            this.translateService.get(this.sectionConfig.localizationPath).pipe(
                take(1)
            ).subscribe(result => {
                if (requestId === this.sectionNameRequestId) {
                    this.sectionName = result;
                }
            })
        }
    }

    private prepareModelToSave(sectionConfig: SectionConfigModel): SectionConfigModel {
        sectionConfig.backgroundColor = sectionConfig.setBackgroundColor ? sectionConfig.backgroundColor : null;
        sectionConfig.brandColor = sectionConfig.setBrandColor ? sectionConfig.brandColor : null;

        if (!sectionConfig.topbarFiltersConfig?.show) {
            sectionConfig.topbarFiltersConfig = {
                show: false,
                servicesFilterConfig: {}
            };
        }

        if(!sectionConfig.noDataLabelConfig?.show) {
            sectionConfig.noDataLabelConfig = undefined;
        }

        if (!sectionConfig.appGroups?.length) {
            sectionConfig.appGroups = null;
        } else {
            sectionConfig.appGroups?.forEach(appGroup => {
                appGroup.backgroundColor = appGroup.setBackgroundColor ? appGroup.backgroundColor : null;
            });
        }

        if (sectionConfig.filter.rules.every(rule => !rule.excludeCategories?.length && !rule.includeCategories?.length && !rule.services?.length)) {
            sectionConfig.filter.rules = [];
        }

        if (!sectionConfig.filter.appIds?.length) {
            sectionConfig.filter.appIds = null;
        }

        if (!sectionConfig.bannersConfig) {
            return sectionConfig;
        }

        if (!sectionConfig.bannersConfig.color && !sectionConfig.bannersConfig.interval && !sectionConfig.bannersConfig.items?.length) {
            sectionConfig.bannersConfig = null;
            return sectionConfig;
        }

        sectionConfig.bannersConfig.color = sectionConfig.bannersConfig.setDotsColor ? sectionConfig.bannersConfig.color : null;
        return sectionConfig;
    }
}
